import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  Shield, Users, Plus, Edit, Trash2, Search, 
  Lock, Unlock, UserCheck, RefreshCw
} from 'lucide-react';
import { 
  fetchAdminUsers, 
  createAdminUser, 
  updateAdminUser, 
  deleteAdminUser,
  AdminUserData 
} from '@/lib/authFetch';
import { toast } from 'sonner';

interface AdminUser extends AdminUserData {
  is_active?: boolean;
  ultimo_login?: string;
  created_at?: string;
}

const UserPermissions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    password: '',
    role: 'operator'
  });

  const roles = [
    {
      name: 'Administrador',
      key: 'admin',
      description: 'Acesso total ao sistema',
      color: 'bg-protec-gold/20 text-protec-gold',
      permissions: [
        'Gerenciar usuários e permissões',
        'Configurar sistema',
        'Acessar todos os relatórios',
        'Gerenciar câmeras',
        'Gerenciar clientes'
      ]
    },
    {
      name: 'Operador',
      key: 'operator', 
      description: 'Operações e monitoramento',
      color: 'bg-protec-gold/20 text-protec-gold',
      permissions: [
        'Visualizar clientes',
        'Gerenciar câmeras',
        'Visualizar relatórios',
        'Monitoramento'
      ]
    }
  ];

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await fetchAdminUsers();
      setUsers(data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user?: AdminUser) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        nome: user.nome || '',
        email: user.email || '',
        password: '', // Não preenchemos a senha na edição
        role: user.role || 'operator'
      });
    } else {
      setEditingUser(null);
      setFormData({
        nome: '',
        email: '',
        password: '',
        role: 'operator'
      });
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.nome || !formData.email) {
      toast.error('Nome e email são obrigatórios');
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error('Senha é obrigatória para novos usuários');
      return;
    }

    setSaving(true);
    try {
      if (editingUser?.id) {
        // Na edição, não enviamos a senha se estiver vazia
        const updateData: Partial<AdminUserData> = {
          nome: formData.nome,
          email: formData.email,
          role: formData.role
        };
        await updateAdminUser(editingUser.id, updateData);
        toast.success('Usuário atualizado com sucesso!');
      } else {
        await createAdminUser(formData);
        toast.success('Usuário criado com sucesso!');
      }
      setModalOpen(false);
      loadUsers();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar usuário');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!userToDelete?.id) return;

    try {
      await deleteAdminUser(userToDelete.id);
      toast.success('Usuário deletado com sucesso!');
      setDeleteModalOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error: any) {
      toast.error(error.message || 'Erro ao deletar usuário');
    }
  };

  const handleToggleStatus = async (user: AdminUser) => {
    if (!user.id) return;
    try {
      await updateAdminUser(user.id, { is_active: !user.is_active });
      toast.success(`Usuário ${user.is_active ? 'desativado' : 'ativado'}`);
      loadUsers();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const getRoleBadge = (role: string) => {
    const roleConfig = roles.find(r => r.key === role);
    if (!roleConfig) return <Badge className="bg-gray-500/20 text-gray-400">Desconhecido</Badge>;
    return <Badge className={roleConfig.color}>{roleConfig.name}</Badge>;
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive 
      ? <Badge className="bg-green-500/20 text-green-400"><Unlock className="w-3 h-3 mr-1" />Ativo</Badge>
      : <Badge className="bg-gray-500/20 text-gray-400"><Lock className="w-3 h-3 mr-1" />Inativo</Badge>;
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    return matchesSearch && matchesRole;
  });

  return (
    <div className="space-y-6">
      {/* Role Definitions */}
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
            <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
            Perfis e Permissões
          </CardTitle>
          <CardDescription className="text-gray-300 text-xs sm:text-sm">
            Definições dos níveis de acesso ao sistema
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {roles.map((role) => (
              <div key={role.key} className="border border-protec-gold/20 rounded-lg p-3 sm:p-4 bg-protec-dark/50">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <h3 className="text-white font-medium text-sm sm:text-base">{role.name}</h3>
                  <Badge className={`${role.color} text-xs`}>{role.key}</Badge>
                </div>
                <p className="text-gray-400 text-xs sm:text-sm mb-2 sm:mb-3">{role.description}</p>
                <div className="space-y-1">
                  {role.permissions.map((permission, index) => (
                    <div key={index} className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-2">
                      <div className="w-1 h-1 bg-protec-gold rounded-full flex-shrink-0"></div>
                      {permission}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* User Management */}
      <Card className="bg-protec-dark-light border-protec-gold/20">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-white flex items-center gap-2 text-base sm:text-lg">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-protec-gold" />
            Usuários do Sistema
          </CardTitle>
          <CardDescription className="text-gray-300 text-xs sm:text-sm">
            Gerencie usuários com acesso ao painel administrativo
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 mb-4 sm:mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar usuários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-protec-dark border-protec-gold/30 text-white text-sm"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="flex-1 sm:flex-none bg-protec-dark border border-protec-gold/30 text-white px-3 py-2 rounded-md text-sm"
              >
                <option value="all">Todos os Perfis</option>
                <option value="admin">Administrador</option>
                <option value="operator">Operador</option>
              </select>
              <Button 
                className="bg-protec-gold text-black hover:bg-protec-gold-light text-sm"
                onClick={() => handleOpenModal()}
              >
                <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Novo Usuário</span>
                <span className="sm:hidden">Novo</span>
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-400 text-sm">Carregando usuários...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Users className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 opacity-50" />
              <p className="text-sm">Nenhum usuário encontrado</p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {filteredUsers.map((user) => (
                <div key={user.id} className="border border-protec-gold/20 rounded-lg p-3 sm:p-4 bg-protec-dark/50">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    {/* Icon + Info */}
                    <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${user.is_active ? 'bg-protec-gold/20' : 'bg-gray-500/20'}`}>
                        <Users className={`w-4 h-4 sm:w-5 sm:h-5 ${user.is_active ? 'text-protec-gold' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium text-sm sm:text-base truncate">{user.nome}</h3>
                        <p className="text-gray-400 text-xs sm:text-sm truncate">{user.email}</p>
                        <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                          {user.ultimo_login 
                            ? `Último login: ${new Date(user.ultimo_login).toLocaleString('pt-BR')}`
                            : 'Nunca acessou'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Badges + Actions */}
                    <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4">
                      <div className="flex sm:flex-col items-center sm:items-end gap-1 sm:space-y-1">
                        {getRoleBadge(user.role)}
                        {getStatusBadge(user.is_active !== false)}
                      </div>
                      <div className="flex gap-1 sm:gap-2">
                        <Button 
                          size="sm" 
                          className="bg-amber-600/20 text-amber-400 hover:bg-amber-600/40 border-0 h-8 w-8 p-0"
                          onClick={() => handleOpenModal(user)}
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-rose-600/20 text-rose-400 hover:bg-rose-600/40 border-0 h-8 w-8 p-0"
                          onClick={() => { setUserToDelete(user); setDeleteModalOpen(true); }}
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Criar/Editar Usuário */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nome Completo *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Nome do usuário"
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
              />
            </div>

            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="email@exemplo.com"
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
              />
            </div>

            <div>
              <Label>{editingUser ? 'Nova Senha (deixe em branco para manter)' : 'Senha *'}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="••••••••"
                className="bg-protec-dark border-protec-gold/30 text-white mt-1"
              />
            </div>

            <div>
              <Label>Perfil de Acesso *</Label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                className="w-full bg-protec-dark border border-protec-gold/30 text-white rounded-md px-3 py-2 mt-1"
              >
                <option value="admin">Administrador</option>
                <option value="operator">Operador</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-protec-gold text-black hover:bg-protec-gold-light w-full"
            >
              {saving ? 'Salvando...' : (editingUser ? 'Atualizar' : 'Criar Usuário')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-protec-dark-light border-protec-gold/20 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <p className="text-gray-300 py-4">
            Tem certeza que deseja excluir o usuário <strong>{userToDelete?.nome}</strong>?
            <br />
            Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteModalOpen(false)}
              className="border-gray-500 text-gray-400"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserPermissions;
